import React, { useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { compose, withState } from "recompose";
import * as R from "ramda";
import { Row } from "antd";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Form } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";
import styled from "styled-components";

import * as gtag from "../../../../../lib/gtag";
import linkedinFeed from "../../../../../assets/linkedin-feed.svg";
import {
  saveOpportunityMultiposting,
  SAVE_OPPORTUNITY_MULTIPOSTING
} from "../../../../../store/actions/opportunity";
import useLinkedinAuth from "../../../../../hooks/social/useLinkedinAuth";
import {
  connectCompanySocialAccount
  // CONNECT_COMPANY_SOCIAL_ACCOUNT
} from "../../../../../store/actions/company";
import {
  connectUserSocialAccount
  // CONNECT_USER_SOCIAL_ACCOUNT
} from "../../../../../store/actions/user";
import Account from "../../../../Company/SocialAccounts/Account";
import {
  multipostingAccounts,
  scraperAccounts
} from "../../../../Company/SocialAccounts/AccountList";

import IntegrationUrl from "./IntegrationUrl";
import JobItem from "./JobItem";
import SocialItem from "./SocialItem";
import FacebookItem from "./FacebookItem";
import { formatMultiposting } from "./selector";
import MultipostingModalHelper from "./MultipostingModalHelper";

const Accounts = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const IntegrationsContainer = styled.div`
  margin-top: 20px;
`;

const Disabled = styled.div`
  position: absolute;
  background: #f7f9fa;
  height: 100%;
  width: 100%;
  z-index: 1;
  opacity: 0.6;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const MultipostingForm = ({
  t,
  form,
  onSubmit,
  newOpportunity,
  facebookSocialConnect,
  linkedinSocialConnect,
  facebookSocialPublished,
  linkedinSocialPublished,
  linkedinSocialErrorPublished,
  facebookSocialErrorPublished,
  parentForm,
  company,
  socialAccounts,
  scrapper,
  scrapperData,
  setScrapper,
  section,
  setForm,
  opportunity,
  isDisabled
}) => {
  const sectionName = useMemo(
    () => t("opportunity:wizard.multiposting.sectionName"),
    [t]
  );
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);

  const dispatch = useDispatch();
  React.useEffect(() => {
    newOpportunity &&
      parentForm({ multiposting: form.getFieldsValue(), scrapper });
  });

  const connectToLinkedin = React.useCallback(
    code => {
      !linkedinSocialConnect &&
        dispatch(
          connectUserSocialAccount({
            code,
            provider: "linkedin",
            redirect_uri: process.env.REACT_APP_LI_REDIRECT_URL
          })
        );
    },
    [dispatch, linkedinSocialConnect]
  );

  const connectToFacebook = React.useCallback(
    ({ access_token, id }) => {
      dispatch(
        connectCompanySocialAccount({
          access_token,
          page_id: id,
          provider: "facebook"
        })
      );
    },
    [dispatch]
  );
  const linkedinAuth = useLinkedinAuth(connectToLinkedin);

  const [showMultipostingModal, setShowMultipostingModal] = React.useState(
    false
  );
  const [MultipostingModal, setMultipostingModal] = React.useState(null);
  const [showScraperModal, setShowScraperModal] = React.useState(false);
  const [integrationType, setIntegrationType] = React.useState({});

  useEffect(() => {
    const multiposting = formatMultiposting(
      provider => R.path(["multiposting", "jobPost", provider], opportunity),
      provider => form.getFieldValue(provider),
      ["facebook", "linkedin"].filter(provider =>
        form.getFieldValue(`${provider}Social`)
      )
    );

    // scrapperData comes from backend (what's been sent), scrapper is what you modified locally.
    const scrapperResult = R.mergeRight(scrapperData, scrapper);

    const data = {
      multiposting: R.when(R.always(!newOpportunity), R.dissoc("socialFeed"))(
        multiposting
      ),
      scrapper: scrapperResult
    };
    setForm && setForm(data);
  }, [form, newOpportunity, opportunity, scrapper, scrapperData, setForm]);

  return (
    <div ref={ref}>
      <Form layout="vertical" onSubmit={onSubmit} hideRequiredMark={true}>
        {newOpportunity ? (
          <h2>{t("opportunity:wizard.multiposting.titleJobNew")}</h2>
        ) : (
          <h2>{t("opportunity:wizard.multiposting.titleJob")}</h2>
        )}
        {!company.allowMultiposting && (
          <p>{t("opportunity:wizard.multiposting.isNotPublic")}</p>
        )}
        <div style={{ position: "relative" }}>
          {(isDisabled || !company.allowMultiposting) && <Disabled />}
          <Row>
            <p>{t("opportunity:wizard.multiposting.simpleTitleJob")}</p>
            <div style={{ display: "flex", marginBottom: 14 }}>
              {
                //// Se oculta hasta resolver lo de FacebookJobs
                /* <JobItem
                    form={form}
                    name={"facebook"}
                    logo={facebook}
                    feedConnected={R.hasPath(
                      ["socialAccounts", "facebook"],
                      company
                    )}
                    onConnect={facebookAuth}
                  /> */
              }
              {multipostingAccounts.map((item, index) => (
                <JobItem
                  key={`${item.provider}-${item.country || index}`}
                  form={form}
                  name={item.provider}
                  logo={item.logo}
                  country={item.country || ""}
                  flag={item.flag}
                  feedConnected={R.hasPath(
                    ["socialAccounts", item.provider],
                    company
                  )}
                  onConnect={() => {
                    setMultipostingModal(item.modal);
                    setIntegrationType(item);
                    setShowMultipostingModal(true);
                  }}
                />
              ))}
            </div>
          </Row>
          <Row>
            <p>{t("opportunity:wizard.multiposting.simpleTitleSocial")}</p>
            <div style={{ display: "flex" }}>
              <SocialItem
                form={form}
                feed={"linkedin"}
                logo={linkedinFeed}
                onConnect={linkedinAuth}
                feedConnected={linkedinSocialConnect}
                name={"linkedinSocial"}
                newOpportunity={newOpportunity}
                datePublished={linkedinSocialPublished}
                errorPublished={linkedinSocialErrorPublished}
              />
              {false && (
                <FacebookItem
                  form={form}
                  connectToFacebook={connectToFacebook}
                  feedConnected={facebookSocialConnect}
                  newOpportunity={newOpportunity}
                  datePublished={facebookSocialPublished}
                  errorPublished={facebookSocialErrorPublished}
                />
              )}
            </div>
          </Row>
          <Row>
            {!R.isEmpty(socialAccounts) && (
              <IntegrationsContainer>
                <h2>
                  {t("opportunity:wizard.multiposting.integrations.mainTitle")}
                </h2>
                <MultipostingModalHelper />
                <p>
                  {t(
                    "opportunity:wizard.multiposting.integrations.mainDescription"
                  )}
                </p>
                <Accounts>
                  {Object.keys(socialAccounts).map(index => {
                    const { country } = socialAccounts[index];
                    const item = R.find(R.propEq("provider", index))(
                      scraperAccounts
                    );
                    if (!item) return null;
                    return (
                      <Account
                        key={item.provider}
                        provider={item.provider}
                        logo={item.logo}
                        account={R.has(item.provider, scrapperData)}
                        filled={R.has(item.provider, scrapper)}
                        country={country || item.country || ""}
                        flag={item.flag}
                        isDisabled={false}
                        multipostingForm
                        modeOtherIntegrations
                        connectedTo={{ name: item.name }}
                        onModalOpen={() => {
                          setIntegrationType(item);
                          setShowScraperModal(true);
                        }}
                      />
                    );
                  })}
                </Accounts>
              </IntegrationsContainer>
            )}
          </Row>
          <br />
          {/* {!isDisabled && newOpportunity && (
          <Row type="flex" justify="end">
            <Button
              loading={loading}
              disabled={newOpportunity && connecting}
              type="primary"
              size="lg"
              htmlType="submit"
              data-test="submit-detail-button"
            >
              {t("global:save")}
            </Button>
          </Row>
        )} */}
        </div>
      </Form>
      {showMultipostingModal && (
        <MultipostingModal
          type={integrationType}
          onClose={() => setShowMultipostingModal(false)}
        />
      )}
      {showScraperModal && (
        <IntegrationUrl
          scrapper={R.mergeRight(scrapperData, scrapper)}
          setScrapper={setScrapper}
          removeScrapper={key => delete scrapperData[key]}
          type={integrationType}
          onClose={() => setShowScraperModal(false)}
        />
      )}
    </div>
  );
};

const enhancer = compose(
  withTranslation(["opportunity", "global"]),
  connect(
    ({ opportunity, company, user }) => ({
      company,
      opportunity: opportunity.selected,
      newOpportunity: opportunity.newOpportunity,
      scrapperData: R.pathOr({}, ["selected", "scrapper"], opportunity),
      socialAccounts: R.omit(
        R.prepend("loading", multipostingAccounts.map(R.prop("provider"))),
        company.socialAccounts
      ),
      linkedinSocialPublished: R.pathOr(
        null,
        ["selected", "multiposting", "socialFeed", "linkedin", "lastPublished"],
        opportunity
      ),
      facebookSocialPublished: R.pathOr(
        null,
        ["selected", "multiposting", "socialFeed", "facebook", "lastPublished"],
        opportunity
      ),
      linkedinSocialErrorPublished: R.pathOr(
        null,
        [
          "selected",
          "multiposting",
          "socialFeed",
          "linkedin",
          "errorPublished"
        ],
        opportunity
      ),
      facebookSocialErrorPublished: R.pathOr(
        null,
        [
          "selected",
          "multiposting",
          "socialFeed",
          "facebook",
          "errorPublished"
        ],
        opportunity
      ),
      linkedinSocialConnect: R.hasPath(["socialAccounts", "linkedin"], user),
      facebookSocialConnect: R.hasPath(["socialAccounts", "facebook"], user)
    }),
    {
      saveOpportunityMultiposting
    }
  ),
  withState("scrapper", "setScrapper", R.propOr({}, "scrapper")),
  withForm({
    selector: ({ opportunity }) => ({
      // Get enabled info of each provider
      ...R.fromPairs(
        multipostingAccounts.map(item => [
          item.provider,
          R.pathOr(
            false,
            ["selected", "multiposting", "jobPost", item.provider, "enabled"],
            opportunity
          )
        ])
      ),
      linkedinSocial: R.includes(
        "linkedin",
        R.pathOr([], ["selected", "multiposting", "socialFeed"], opportunity)
      ),
      facebookSocial: R.includes(
        "facebook",
        R.pathOr([], ["selected", "multiposting", "socialFeed"], opportunity)
      )
    }),
    onSubmit: (
      _,
      {
        saveOpportunityMultiposting,
        opportunity,
        form,
        newOpportunity,
        scrapper,
        scrapperData
      }
    ) => {
      form.validateFields(err => {
        if (!err) {
          gtag.event({
            category: "VacancyDetail",
            action: "VacancyDetail.Multiposting-Save",
            label: opportunity.id
          });

          const multiposting = formatMultiposting(
            provider =>
              R.path(["multiposting", "jobPost", provider], opportunity),
            provider => form.getFieldValue(provider),
            ["facebook", "linkedin"].filter(provider =>
              form.getFieldValue(`${provider}Social`)
            )
          );

          // scrapperData comes from backend (what's been sent), scrapper is what you modified locally.
          const scrapperResult = R.mergeRight(scrapperData, scrapper);

          saveOpportunityMultiposting({
            id: opportunity.id,
            formData: {
              multiposting: R.when(
                R.always(!newOpportunity),
                R.dissoc("socialFeed")
              )(multiposting),
              scrapper: scrapperResult
            }
          });
        }
      });
    }
  }),
  isLoading(SAVE_OPPORTUNITY_MULTIPOSTING)
  // isLoading(CONNECT_COMPANY_SOCIAL_ACCOUNT || CONNECT_USER_SOCIAL_ACCOUNT, {
  //   prop: "connecting"
  // })
);

export default enhancer(MultipostingForm);
